/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICommerceApiSettings
} from '@msdyn365-commerce/core';
import { Customer, CustomerAttribute } from '@msdyn365-commerce/retail-proxy';
import { readAsync, updateAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CustomersDataActions.g';

import { buildCacheKey } from '.';
import { GetCustomerInput } from './get-customer';

export interface IUpdateCustomerAttributesInput extends IActionInput {
    phone?: string;
    vatNumber?: string;
    customerAttributes: CustomerAttribute[];
    userAccountNumber: string;
    apiSettings: ICommerceApiSettings;
}

export enum ContactInfoType {
    None = 0,
    Phone = 1,
    Email = 2,
    Url = 3,
    Telex = 4,
    Fax = 5
}

/**
 *  Input class for the updateCustomerAttributes.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export class updateCustomerAttributesInput implements IUpdateCustomerAttributesInput {
    public userAccountNumber: string;

    public customerAttributes: CustomerAttribute[];

    public apiSettings: ICommerceApiSettings;

    public phone?: string;

    public vatNumber?: string;

    constructor(
        userAccountNumber: string,
        customerAttributes: CustomerAttribute[],
        apiSettings: ICommerceApiSettings,
        phone?: string,
        vatNumber?: string
    ) {
        this.userAccountNumber = userAccountNumber;
        this.apiSettings = apiSettings;
        this.customerAttributes = customerAttributes;
        this.phone = phone;
        this.vatNumber = vatNumber;
    }

    public getCacheKey = (): string => buildCacheKey(`${this.userAccountNumber}`, this.apiSettings);

    public getCacheObjectType = () => 'Customer';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * The action method for the updateCustomerAttributes data action.
 * @param input The action input.
 * @param ctx The action context.
 */
export async function updateCustomerAttributesAction(input: IUpdateCustomerAttributesInput, ctx: IActionContext): Promise<Customer> {
    const { customerAttributes, phone, vatNumber, userAccountNumber } = input;

    const customer = await readAsync({ callerContext: ctx, bypassCache: 'get' }, userAccountNumber);

    if (!customer) {
        ctx.telemetry.debug('[update-customer-attributes] Unable to find customer');
        throw new Error('Unable to find customer');
    }

    // The customer level phone (customer.phone) is the primary phone number. We need to update the primary phone in customer.Contacts as well.
    const primaryContact = customer.Contacts?.filter(
        contact => contact && contact.IsPrimary && contact.AddressTypeValue === ContactInfoType.Phone
    )?.[0];

    if (primaryContact) {
        primaryContact.Value = phone;
    }

    const newCustomer = { ...customer };
    newCustomer.Phone = phone;
    newCustomer.PhoneRecordId = primaryContact?.RecordId ? primaryContact?.RecordId : 0;
    newCustomer.VatNumber = vatNumber;
    newCustomer.Attributes = customerAttributes;

    const updatedCustomer = await updateAsync({ callerContext: ctx }, newCustomer);

    if (!updatedCustomer) {
        ctx.telemetry.debug('[update-customer-attributes] Unable to update customer attributes');
        throw new Error('Unable to update customer attributes');
    }

    ctx.update(new GetCustomerInput(ctx.requestContext.apiSettings), updatedCustomer);

    return updatedCustomer;
}

/**
 * The updateCustomerAttributes data action
 * Returns the updated customer.
 */
export const updateCustomerAttributesActionDataAction = createObservableDataAction<Customer>({
    id: '@msdyn365-commerce-modules/retail-actions/update-customer-attributes',
    action: <IAction<Customer>>updateCustomerAttributesAction
});

export default updateCustomerAttributesActionDataAction;
